import { ipaToFeatures } from "../phonemes/ipa";
import { unitToOutput } from "../units/applyToIpa";
import { parseUnits } from "../units/parseUnits";
import { isPunctuation, NoteClass, WordUnit } from "../units/wordUnit";
import { en_to_PIE } from "./en_to_PIE.json";
import { en_UK_Overrides } from "./en_UK-overrides";
import { en_UK_to_ipa } from "./en_UK.json";
import { en_US_Abbrevs } from "./en_US-abbrev";
import { en_US_Overrides } from "./en_US-overrides";
import { en_US_to_ipa } from "./en_US.json";
import { handleNumbers } from "./numberToEnglish";

export type EnglishDictionaries =
  /** IPA describing the general American accent */
  | 'en_US'
  /** IPA describing British Received Pronunciation */
  | 'en_UK'
  /** English to Proto-Indo-European */
  | 'en_PIE';

/** punctuation that gets treated as a 'word', preserved thru rules, and treated specially by output */
export const plainPunctuation = [',', '.', '?', '!', '|', '‖'];
export const nonIpaPunctuation = [',', '.', '?', '!'];

/**
 * convert English words into IPA phonemes
 * @param fallback if word isn't found in dictionary, caller can provide an alternate method of lookup
 * @returns [sentence in ipa, list of words it couldn't find]
 */
export function wordsToIpa(input: string | WordUnit[], iso?: EnglishDictionaries, fallback?: (word: string) => string): [string, string[]] {
  const abbrevs: Record<string, string> = iso === 'en_PIE' ? {} : en_US_Abbrevs;
  const units = Array.isArray(input) ? input
    : parseUnits(input, abbrevs, { punctuation: true, pmd: true, lowerCase: true, cleanLigatures: true });

  const substitution = units.map(unit => isPunctuation(unit) ? unit : handleNumbers(unit));
  const ipaWords = substitution.map(unit => findOrFallback(unit, iso ?? 'en_US', fallback));
  const finalString = ipaWords.filter(result => result.unit).map(result => unitToOutput(result.unit, true)).join(' ');

  const notFound = ipaWords.map((result, i) => !result.found ? units[i] : undefined);
  const onlyNotFound = (notFound.filter(word => word !== undefined) as WordUnit[]).map(u => u.input);

  return [finalString, onlyNotFound];
}

/** first look in dictionaries, if not present, use 'fallback' if present  */
function findOrFallback(unit: WordUnit, iso: EnglishDictionaries, fallback?: (word: string) => string): Result {
  if (unit.notes && unit.notes[0] === NoteClass.AsIs) {
    return { unit, found: true };
  } else if (isPunctuation(unit)) {
    return { unit, found: true };
  }

  /* this seems to get it wrong more than it gets it right
  // special handling of English <A>
  if (unit.input === 'a' && unit.notes && unit.notes.includes(NoteClass.AllCaps) && iso !== 'en_PIE') {
    return { unit: { ...unit, input: "ˈeɪ̯" }, found: true };
  }
  */
  // special handling of <US>
  const US = (unit.input === 'us' && unit.notes && unit.notes.includes(NoteClass.AllCaps));

  // look up in dictionary
  const find = getOne(unit.input, iso);
  if (find !== undefined && !US) {
    return { unit: { ...unit, input: find }, found: true };
  }

  // if ALLCAPS, pronounce the letters
  if (unit.notes && unit.notes.includes(NoteClass.AllCaps)) {
    const result = spellWord(unit.input, iso);
    return result === '' ? { found: false } : { unit: { ...unit, input: result }, found: true };
  }

  // if hyphenated, try each word
  if (unit.notes && unit.notes.includes(NoteClass.Hyphenated)) {
    const result = breakOnHyphens(unit, iso, fallback);
    if (result) {
      return result;
    }
  }

  // if word alternates letter/period, assume it's an abbreviation & pronounce each letter
  const asAbbrev = readAlternating(unit.input, iso);
  if (asAbbrev) {
    return { unit: { ...unit, input: asAbbrev }, found: true };
  }

  // e.g., if word ends in <s> but isn't in dictionary, try it w/out <s>
  const testEnding = tryAffixes(unit.input, iso);
  if (testEnding !== undefined) {
    return { unit: { ...unit, input: testEnding }, found: true };
  }

  // use whatever fallback is given
  const result = fallback !== undefined && unit.input !== '' ? fallback(unit.input) : undefined;
  return { unit: result ? { ...unit, input: result } : undefined, found: false };
}
interface Result {
  readonly unit?: WordUnit;
  readonly found: boolean;
}

/** find a single pronunciation, in a dictionary, possibly looking at backup dictionaries */
function getOne(word: string, iso: EnglishDictionaries): string | undefined {
  if (plainPunctuation.includes(word)) {
    return word;
  }
  if (iso === 'en_US' || iso === 'en_UK') {
    const dictionary = iso === 'en_UK' ? en_UK_Overrides : en_US_Overrides;
    const override = verify(dictionary[word]);
    if (override !== undefined) {
      const ipa = override[0] === '/' ? override.slice(1, override.length - 1) : override;
      return fixupSymbols(ipa, iso);
    }
  }

  const dictionary = iso === 'en_UK' ? en_UK_to_ipa : iso === 'en_PIE' ? en_to_PIE : en_US_to_ipa;
  const result = verify(dictionary[word]);
  if (result === undefined) {
    return undefined;
  }
  // grab the first entry, e.g., "/ˈðɪs/, /ðɪs/" or "/ˈtɛst/"
  const second = result.indexOf('/', 1);
  const actual = second === -1 ? result : result.slice(1, second);
  return fixupSymbols(actual, iso);
}

/** turn pronunciation into a canonical form */
function fixupSymbols(ipa: string, iso: EnglishDictionaries): string {
  // UK dictionary uses a different /l/
  const withL = iso === 'en_UK' ? ipa.replace('l', 'ɫ') : ipa;
  // explicitly mark English diphthongs: aɪ̯ aʊ̯ eɪ̯ oʊ̯ ɔɪ̯
  const dipth1 = withL.replace(/aɪ/g, 'aɪ̯');
  const dipth2 = dipth1.replace(/aʊ/g, 'aʊ̯');
  const dipth3 = dipth2.replace(/eɪ/g, 'eɪ̯');
  const dipth4 = dipth3.replace(/oʊ/g, 'oʊ̯');
  const dipth5 = dipth4.replace(/ɔɪ/g, 'ɔɪ̯');
  // UK dictionary uses a zero width joiner for some diphthongs: aɪ̯ aʊ̯ eɪ̯ ɔɪ̯ əʊ̯ eə̯ iə̯
  // but the UK-override dictionary doesn't require it other than for /i‍ə/
  const dipth6 = dipth5.replace(/a‍ɪ/g, 'aɪ̯');
  const dipth7 = dipth6.replace(/a‍ʊ/g, 'aʊ̯');
  const dipth8 = dipth7.replace(/e‍ɪ/g, 'eɪ̯');
  const dipth9 = dipth8.replace(/ɔ‍ɪ/g, 'ɔɪ̯');
  const dipth10 = dipth9.replace(/(ə‍ʊ)|(əʊ)/g, 'əʊ̯');  // plain version not handled above
  const dipth11 = dipth10.replace(/(e‍ə)|(eə)/g, 'eə̯'); // plain version not handled above
  const dipth12 = dipth11.replace(/i‍ə/g, 'iə̯');  // ZWJ required since words like <amiable> have /iə/ not /iə̯/
  // affricates
  const aff1 = dipth12.replace(/(tʃ)|(t‍ʃ)/g, 't͡ʃ');
  const aff2 = aff1.replace(/(dʒ)|(d‍ʒ)/g, 'd͡ʒ');
  const aff3 = aff2.replace(/dz/g, 'd͡z');
  // [[ts]] sounds like [[t+s^]], not [[t+s]], so don't replace this anymore
  // const aff4 = aff3.replace(/ts/g, 't͡s');
  return aff3;
}

/** JavaScript will obnoxiously return a function for thing['constructor'] */
function verify(lookup: string): string | undefined {
  return typeof lookup === 'string' ? lookup : undefined;
}

/** pronounce all the letters & numbers in the word */
function spellWord(word: string, iso: EnglishDictionaries): string {
  let spelled = '';
  for (let i = 0; i < word.length; i++) {
    const c = word.charAt(i);
    const c2 = ('0' <= c && c <= '9') ? handleNumbers({ input: c }).input : c;
    const ipa = getOne(c2, iso);
    if (ipa) {
      // special handling for a > /ə/ vs. /ˈeɪ̯/
      spelled += ipa === 'ə' ? 'ˈeɪ̯' : ipa;
    }
  }
  return spelled;
}

/** try each word separately */
function breakOnHyphens(unit: WordUnit, iso: EnglishDictionaries, fallback?: (word: string) => string): Result | undefined {
  const words = unit.input.split('-');
  // just in case there are numbers mixed in with the words
  const wordsPlus = words.flatMap(w => {
    const numbers = handleNumbers({ input: w });
    if (numbers.notes && numbers.notes.includes(NoteClass.Hyphenated)) {
      return numbers.input.split('-');
    } else {
      return numbers.input;
    }
  });

  let result = '';
  let found = true;
  for (const w of wordsPlus) {
    const a = findOrFallback({ input: w }, iso, fallback);
    if (!a.unit) {
      return undefined;
    }
    result += a.unit.input;
    if (!a.found) found = false;
  }
  return { unit: { ...unit, input: result }, found };
}

/** if word alternates letter/period, read the letters, assuming it's an abbreviation */
function readAlternating(word: string, iso: EnglishDictionaries): string | false {
  // first check if it alternates char/period
  const len = word.length;
  if (len === 0 || len % 2 === 1) {
    return false;
  }
  for (let i = 0; i < len; i += 2) {
    if (word[i] === '.' || word[i + 1] !== '.') {
      return false;
    }
  }

  // pronounce every odd character
  let result = '';
  for (let i = 0; i < len; i += 2) {
    const one = getOne(word.substring(i, i + 2), iso);
    if (one === undefined) {
      return false; // couldn't pronounce the character
    }
    result += one;
  }
  return result;
}

/** if word is in dictionary without certain prefixes or postfixes, build up pronunciation */
function tryAffixes(word: string, iso: EnglishDictionaries): string | undefined {
  const checkS = tryS(word, iso);
  if (checkS) {
    return checkS;
  }
  const prefixes = [['non', 'ˌnɑn'], ['re', 'ˌɹi'], ['un', 'ˌən']];
  for (const prefix of prefixes) {
    const check = tryPrefix(word, iso, prefix[0], prefix[1]);
    if (check) {
      return check;
    }
  }
  const suffixes = [['ify', 'ɪfaɪ̯'], ['ly', 'ɫi'], ['ness', 'nɛs'], ['ing', 'ɪŋ']];
  for (const suffix of suffixes) {
    const check = trySuffix(word, iso, suffix[0], suffix[1]);
    if (check) {
      return check;
    }
  }

  return undefined;
}

function tryS(word: string, iso: EnglishDictionaries): string | undefined {
  if (!word.endsWith('s')) {
    return undefined;
  }

  const lookup = lookupNoS(word, iso);
  if (!lookup) {
    return undefined;
  }

  const end = lookup[lookup.length - 1];
  const endFeatures = ipaToFeatures(end);
  const add =
    end === 's' || end === 'z' || end === 'ʃ' || end === 'ʒ' ? 'əz'
      : endFeatures?.includes('voiceless') ? 's'
        : 'z';

  return lookup + add;
}
function lookupNoS(word: string, iso: EnglishDictionaries): string | undefined {
  // first see if we should remove/change multiple letters
  const subword =
    word.endsWith("xes") || word.endsWith("'s") ? word.substring(0, word.length - 2)
      : word.endsWith("ies") ? word.substring(0, word.length - 3) + "y"
        : word.endsWith("sses") || word.endsWith("shes") || word.endsWith("ches") ? word.substring(0, word.length - 2)
          : undefined;
  if (subword !== undefined) {
    const lookup = getOne(subword, iso);
    if (lookup !== undefined) {
      return lookup;
    }
  }
  // just remove final <s>
  return getOne(word.substring(0, word.length - 1), iso);
}

/** see if 'word' without 'prefix' is in dictionary - if so, add 'ipa' to the start */
function tryPrefix(word: string, iso: EnglishDictionaries, prefix: string, ipa: string): string | undefined {
  if (!word.startsWith(prefix)) {
    return undefined;
  }

  const subword = word.substring(prefix.length);
  const lookup = getOne(subword, iso);
  if (!lookup) {
    return undefined;
  }

  return ipa + lookup;
}

/** see if 'word' without 'suffix' is in dictionary - if so, add 'ipa' to the end */
function trySuffix(word: string, iso: EnglishDictionaries, suffix: string, ipa: string): string | undefined {
  if (!word.endsWith(suffix)) {
    return undefined;
  }

  const subword = word.substring(0, word.length - suffix.length);
  const lookup = getOne(subword, iso);
  if (!lookup) {
    return undefined;
  }

  return lookup + ipa;
}
